import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
import {useCallback, useRef, useState} from 'react'
import {AddCommentEditorNotDiffLines} from '@github-ui/diff-lines/add-comment-editor'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useCreateReplyCommentMutation} from '../../../mutations/use-create-reply-comment-mutation'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../../page-data/loaders/use-thread-previews'
import {useSelectedRange} from '../../../stores/selectors'

export function ThreadReplyComposer({thread, startExpanded = false}: {thread: Thread; startExpanded?: boolean}) {
  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const {baseOid: comparisonStartOid, headOid: comparisonEndOid} = useSelectedRange()
  const {mutate: createReplyComment} = useCreateReplyCommentMutation(filePath, lineKey)
  const [isReplying, setIsReplying] = useState(startExpanded)
  const composerRef = useRef<HTMLDivElement>(null)

  const submitReply = useCallback(
    ({
      commentText,
      onCompleted,
      onError,
      submitBatch,
    }: {
      commentText: string
      onCompleted?: () => void
      onError: (error: Error) => void
      submitBatch?: boolean
    }) => {
      const inReplyTo = thread.commentsData?.comments.at(-1)?.databaseId
      if (!inReplyTo) {
        onError(new Error('Missing reply target comment'))
        return
      }

      createReplyComment(
        {
          text: commentText,
          inReplyTo,
          submitBatch: submitBatch ?? false,
          path: filePath,
          comparisonStartOid,
          comparisonEndOid,
        },
        {
          onError,
          onSuccess: () => onCompleted?.(),
        },
      )
    },
    [comparisonEndOid, comparisonStartOid, createReplyComment, filePath, thread.commentsData],
  )

  return (
    <div className="px-2 pb-2 bgColor-inset" ref={composerRef}>
      {isReplying ? (
        <AddCommentEditorNotDiffLines
          batchingEnabled
          condensed
          focusOnMount
          filePath={filePath}
          isReplying
          threadId={thread.id}
          onAddComment={submitReply}
          onCancelComment={() => setIsReplying(false)}
        />
      ) : (
        <CompactCommentButton onClick={() => setIsReplying(true)}>Write a reply</CompactCommentButton>
      )}
    </div>
  )
}
